/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * CategoryComponent.tsx
 */

import {PolicyListener} from "../../services/PolicyService";
import {RouteComponentProps} from 'react-router-dom';
import Policy from "../../tools/provider/Policy";
import HiloReactComponent from "../../tools/view/layout/HiloReactComponent";
import React from "react";
import PageTitle, {TitleTab} from "../../tools/view/layout/PageTitle";
import Category from "../../models/Category";
import CategoryForm from "../../models/form/category/CategoryForm";
import UIToast from "../../tools/view/toast/UIToast";
import KareviHTTPService from "../../tools/service/KareviHTTPService";
import {ListWatcher} from "../../tools/list/ListAdapter";
import FormSection from "../../tools/view/form/FormSection";
import {CategoryDTO} from "../../models/dto/category/CategoryDTO";
import {FormListener} from "../../tools/model/form/AbstractForm";
import VideoService from "../../services/VideoService";
import Video from "../../models/Video";
import PageComponent from "../../tools/view/layout/PageComponent";
import FormInput, {Type} from "../../tools/view/form/FormInput";
import {TextField} from "../../tools/model/form/TextField";
import CategoryService from "../../services/CategoryService";
import List from "../../tools/list/List";
import FormModal from "../../tools/view/layout/FormModal";
import VideoMetaDataForm from "../../models/form/video/VideoMetaDataForm";
import VideoAdapter, {VideoAdapterListener} from "../adapter/VideoAdapter";
import VideoDataProvider from "../../provider/VideoDataProvider";
import ReactDOM from "react-dom";
import NavigationService from "../../tools/service/NavigationService";

interface Props extends RouteComponentProps<MatchParams> {
}

interface MatchParams {
    id: string;
}

interface State {
    policy: Policy;
    pageIndex: number,
    category?: Category;
    categoryForm: CategoryForm;
    videoForm: VideoMetaDataForm;
    videoAdapter: VideoAdapter;
    videoDataProvider: VideoDataProvider;
}

export default class CategoryPage extends HiloReactComponent<Props, State> implements PolicyListener, ListWatcher, FormListener<CategoryForm, CategoryDTO>, VideoAdapterListener {

    private readonly _categoryService: CategoryService;
    private readonly _videoService: VideoService;
    private readonly _httpService: KareviHTTPService;
    private readonly _navigationService: NavigationService;
    private _modalContainer?: Element;

    /**
     * constructor
     * @param props
     * @param state
     */
    constructor(props: Props, state: State) {
        super(props, state);

        this._categoryService = this._serviceRepository.resolve(CategoryService.id) as CategoryService
        this._videoService = this._serviceRepository.resolve(VideoService.id) as VideoService
        this._httpService = this._serviceRepository.resolve(KareviHTTPService.id) as KareviHTTPService
        this._navigationService = this._serviceRepository.resolve(NavigationService.id) as NavigationService

        let videoProvider = new VideoDataProvider(this._videoService, this._httpService, state.category)

        this.state = {
            policy: this._policyService.policy,
            pageIndex: 0,
            category: undefined,
            categoryForm: new CategoryForm(),
            videoForm: new VideoMetaDataForm(),
            videoDataProvider: videoProvider,
            videoAdapter: new VideoAdapter(this._t, videoProvider, this)
        };
    }

    /**
     * onUpdate
     */
    public onUpdate(): void {
        this.forceUpdate();
    }

    /**
     * componentDidMount
     */
    public componentDidMount(): void {
        super.componentDidMount();
        this._policyService.addListener(this);

        this._modalContainer = document.querySelector('#modal-container') || undefined;
        this.state.categoryForm.addListener(this)

        this._refreshCategory();
    }

    /**
     * _refreshCategory
     */
    private _refreshCategory = async () => {
        let category = await this._categoryService.findById(this.props.match.params.id);

        this.state.videoDataProvider.category = category

        await this.state.categoryForm.load(CategoryDTO.fromCategory(category));

        await this.setState({category: category});
    };

    public componentWillUnmount(): void {
        this.state.categoryForm.removeListener(this);
        this._policyService.removeListener(this);
        super.componentWillUnmount();
    }

    /**
     * componentDidUpdate
     * @param prevProps
     * @param prevState
     * @param snapshot
     */
    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps.match.params.id !== this.props.match.params.id) {
            this._refreshCategory();
        }
    }


    /**
     * policyUpdated
     * @param policy
     */
    public policyUpdated = (policy: Policy) => {
        this.setState({
            policy: policy
        })
    };

    /**
     * _categoryView
     * @returns {any}
     * @private
     */
    private _categoryView = (props: { form: CategoryForm, category?: Category }) => {
        let categoryIdField = new TextField("category_id", t => t.categoryId, _ => true, Type.TEXT, false, props.category?.id, "", true)

        return <>
            <FormInput field={categoryIdField}/>
            <FormSection label={this.t.information} form={props.form} submit={this._saveCategory}/>
        </>
    };

    /**
     * _displayAddVideoModal
     * @private
     */
    private _displayAddVideoModal = async () => {
        if (!this._modalContainer || !this.state.videoForm) {
            return;
        }

        this.state.videoForm._clearForm();

        const modal = <FormModal
            emptyForm={this.state.videoForm}
            title={t => t.newVideo}
            onSaveClicked={this._saveVideo}
        />;

        ReactDOM.render(modal, this._modalContainer);
    }


    /**
     * _saveVideo
     * @param dto
     * @private
     */
    private _saveVideo = async (dto: Map<string, any> | FormData) => {
        const {category} = this.state;
        if (!category) return console.log("category is undefined.");

        try {
            await this._videoService.createVideo(category, dto);
            this.state.videoForm.statusCode = 200;
            UIToast.default.show(this.t.videoCreated)
            await this._refreshCategory();
        } catch (e) {
            this.state.videoForm.statusCode = e.status
            console.error(e);
        }
    };

    /**
     * _videosView
     * @returns {any}
     * @private
     */
    private _videosView = () => {
        const {videoAdapter, videoDataProvider} = this.state;

        return <List adapter={videoAdapter} dataProvider={videoDataProvider}
                     onAddClick={this._displayAddVideoModal}/>
    };

    /**
     * _saveCategory
     * @param dto
     * @private
     */
    private _saveCategory = async (dto: Map<string, any> | FormData) => {
        const {category} = this.state;
        if (!category) return console.log("category is undefined.");

        try {
            await this._categoryService.updateCategory(category, dto);
            this.state.categoryForm.statusCode = 200;
            UIToast.default.show(this.t.categoryEdited)
            await this._refreshCategory();
        } catch (e) {
            this.state.categoryForm.statusCode = e.status
            console.error(e);
        }
    };

    /**
     * onFormUpdate
     * @param form
     */
    public onFormUpdate = (form: CategoryForm) => {
        this.setState({categoryForm: form})
    };

    /**
     * rowSelected
     * @param obj
     */
    public rowSelected = (obj: Video) => {
        if (!this.state.category) return

        this._navigationService.load(`/category/${this.state.category.id}/video/${obj.id}`)
    };

    /**
     * render
     */
    public render(): any {
        const {category, categoryForm} = this.state;
        const titleTabs: TitleTab[] = [
            {
                title: t => t.details, content: <>
                    <this._categoryView form={categoryForm} category={category}/>
                    <this._videosView/>
                </>
            },
        ];

        return <PageComponent>
            <PageTitle title={t => t.categories}
                       tabs={titleTabs}
                       onTabSelected={index => this.setState({pageIndex: index})}
                       subTitle={category?.title || this.t.loading}
                       backLabel={t => t.allCategories}
            />
            {titleTabs[this.state.pageIndex].content}
        </PageComponent>
    }
}
